import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

interface AuthContextType {
  isLoggedIn: boolean;
  username: string | null;
  sessionExpired: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  username: null,
  sessionExpired: false,
  login: async () => ({ success: false }),
  logout: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem('auth_token');
    if (stored) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${stored}`;
    }
    return stored;
  });
  const [username, setUsername] = useState<string | null>(
    () => localStorage.getItem('auth_username')
  );
  const [sessionExpired, setSessionExpired] = useState(false);
  const logoutRef = useRef<() => void>(() => {});

  const login = useCallback(async (username: string, password: string) => {
    try {
      const response = await axios.post(
        'https://kaskas.pro/split-bill-server/admin/login',
        { username, password }
      );
      const { token: newToken, username: user } = response.data;
      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('auth_username', user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUsername(user);
      setSessionExpired(false);
      return { success: true };
    } catch (error: any) {
      const message = error?.response?.data?.error || 'שגיאה בהתחברות';
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_username');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUsername(null);
    setSessionExpired(false);
  }, []);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 && logoutRef.current) {
          const wasLoggedIn = !!localStorage.getItem('auth_token');
          logoutRef.current();
          if (wasLoggedIn) {
            setSessionExpired(true);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn: !!token, username, sessionExpired, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
